import { styled } from "@mui/material";
import { SearchMatchScope } from "_shared/_views/tables/search-match";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import DebouncedTextField from "_shared/_views/text-input/debounced-text-field";
import Guid from "_shared/guids/guid";
import { credentialsMissingReason } from "marketplace/individuals/profiles/professional-credentials";
import { createMRTColumnHelper, type MRT_TableOptions } from "material-react-table";
import React, { useMemo, useState } from "react";
import UserCoworkerQuery, {
  USER_COWORKER_QUERY_MAXIMUM_LENGTH,
  UserCoworkerFilterParameters,
  UserCoworkerOrderParameters,
  UserCoworkerOrderField,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import UserCoworker from "users/information/coworker-information/user-coworker";
import { MRTNoMemo } from "_shared/_views/tables/table";
import {
  ROW_CHOICE_CELL_PROPS,
  ROW_CHOICE_TABLE_OPTIONS,
  RowChoiceButton,
  RowChoicePerson,
  rowChoiceRowProps,
  type RowChoiceUnavailableReason,
} from "_shared/_views/tables/row-choice";
import { useRowChoiceRequiresCredentials } from "_shared/_views/tables/row-choice-requires-credentials";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import useInfiniteUserCoworkers from "users/information/coworker-information/_hooks/use-infinite-user-coworkers";

const CoworkerTableRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  height: "100%",
  "& .UserCoworkers-tableContainer": {
    flex: 1,
  },
  "& .UserCoworkers-tablePaper": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
}));
const ErrorContent = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
}));

type UserCoworkersProps = {
  selectedMemberUserIds?: Guid[];
  onSelected: (rowData: UserCoworker) => void;
};

const columnHelper = createMRTColumnHelper<UserCoworker>();

/** A picker is read down its names, so it lists coworkers A to Z; a blank search asks for everybody. */
function createQuery(search: string): UserCoworkerQuery {
  return new UserCoworkerQuery(
    new UserCoworkerFilterParameters(),
    new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
    PaginationParameters.DEFAULT,
    search.trim() || undefined,
  );
}

export default function UserCoworkers(props: Readonly<UserCoworkersProps>) {
  const { selectedMemberUserIds, onSelected } = props;
  const requiresCredentials = useRowChoiceRequiresCredentials();
  const reasonIdPrefix = React.useId();

  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  // A stability guarantee, not defensive memoization: the hook starts again from the first page
  // whenever the query changes, so a new query on every render would never get past it.
  const query = useMemo(() => createQuery(search), [search]);
  const { coworkers, isLoading, isInitialLoading, error, loadMore, retry } = useInfiniteUserCoworkers(query);
  useInfiniteScroll(loadMore, coworkers.hasNextPage, isLoading, tableContainerRef);

  function isAlreadyChosen(coworker: UserCoworker): boolean {
    return selectedMemberUserIds?.some((id) => id.isEqualTo(coworker.userId)) ?? false;
  }

  // A team will not take a member whose profile lists no professional credentials (#5800), and the
  // API says which coworkers those are. Only `false` declines: a coworker it said nothing about is
  // left to the API to decide when the team is saved.
  function lacksCredentials(coworker: UserCoworker | undefined): boolean {
    return requiresCredentials && coworker?.hasCredentials === false;
  }

  function getUnavailableReason(coworker: UserCoworker | undefined): RowChoiceUnavailableReason | undefined {
    if (!coworker?.userId || isAlreadyChosen(coworker) || !lacksCredentials(coworker)) return undefined;
    return { id: `${reasonIdPrefix}-${coworker.userId.value}`, text: credentialsMissingReason };
  }

  // Undefined for somebody already chosen or a team would refuse, which is what makes their row and
  // its button say so. MRT asks for the props of the placeholder rows it draws while loading too,
  // whose `original` is an empty object, so a row with no record behind it is one that cannot be
  // chosen rather than a crash.
  function getChoice(coworker: UserCoworker | undefined): (() => void) | undefined {
    if (!coworker?.userId || isAlreadyChosen(coworker) || lacksCredentials(coworker)) return undefined;
    return () => onSelected(coworker);
  }

  const columns = [
    columnHelper.accessor((coworker) => coworker.name?.fullName ?? "", {
      header: "Name",
      id: "name",
      Cell: ({ row }) => (
        <RowChoicePerson
          individual={row.original.asIndividual()}
          name={row.original.name?.fullName ?? "Unknown coworker"}
          // The search reads name and email.
          details={[{ text: row.original.email?.toString(), searched: true }]}
          unavailableReason={getUnavailableReason(row.original)}
        />
      ),
    }),
    columnHelper.display({
      id: "choose",
      header: "",
      muiTableBodyCellProps: ROW_CHOICE_CELL_PROPS,
      Cell: ({ row }) => (
        <RowChoiceButton
          name={row.original.name?.fullName ?? "this coworker"}
          onChoose={getChoice(row.original)}
          alreadyChosen={isAlreadyChosen(row.original)}
          describedBy={getUnavailableReason(row.original)?.id}
        />
      ),
    }),
  ];

  const tableOptions: MRT_TableOptions<UserCoworker> = {
    ...ROW_CHOICE_TABLE_OPTIONS,
    columns,
    data: coworkers.data,
    muiTableBodyRowProps: ({ row }) => rowChoiceRowProps(getChoice(row.original)),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: error
      ? {
        color: "error",
        children: (
          <ErrorContent>
            Error loading data
            <Button color="inherit" size="small" onClick={retry}>Retry</Button>
          </ErrorContent>
        ),
      }
      : undefined,
    muiTableContainerProps: { className: "UserCoworkers-tableContainer", ref: tableContainerRef },
    muiTablePaperProps: { className: "UserCoworkers-tablePaper" },
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: Boolean(error),
      // The search box is outside the table, so the table cannot tell that a search left it empty.
      hasPageFilters: Boolean(query.query),
      isLoading: isInitialLoading,
      onRetry: retry,
      subject: "coworkers",
    }),
    state: {
      isLoading: isInitialLoading,
      showAlertBanner: Boolean(error),
      showProgressBars: isLoading && !isInitialLoading,
    },
  }

  return (
    <CoworkerTableRoot>
      <DebouncedTextField
        label="Search your coworkers"
        size="small"
        type="search"
        value={search}
        onDebouncedChange={setSearch}
        slotProps={{ htmlInput: { maxLength: USER_COWORKER_QUERY_MAXIMUM_LENGTH } }}
      />
      {/* The picker's own search box, lent to the names it marks (#6590). */}
      <SearchMatchScope term={search}>
        <MRTNoMemo<UserCoworker> options={tableOptions} />
      </SearchMatchScope>
    </CoworkerTableRoot>
  );
}
